import { useEffect, useState } from "react";
import AsyncSelect from "react-select/async";
import {
    createSocietyAdmin,
    updateSocietyAdmin,
} from "../../services/SocietyAdminServices";
import { getSocieties } from "../../services/societyService";
import { PasswordField, TextInput, Button } from "../ui";
import { societyAdminSchema, zodErrorToFirstMessage } from "../../schemas/formSchemas";
import styles from "./SocietyAdminForm.module.css";

const selectStyles = {
    control: (base) => ({
        ...base,
        minHeight: 42,
        borderRadius: 12,
        boxShadow: "none",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
};

const SocietyAdminForm = ({
    society,
    societies = [],
    readOnly = false,
    onSuccess,
}) => {
    const [form, setForm] = useState({
        society_admin_name: "",
        society_id: "",
        email: "",
        phone: "",
        password_hash: "",
    });
    const [error, setError] = useState("");

    // Populate for edit/view
    useEffect(() => {
        if (society) {
            setForm({
                society_admin_name: society.society_admin_name || "",
                society_id: society.society_id || "",
                email: society.email || "",
                phone: society.phone || "",
                password_hash: "",
            });
            setError("");
            return;
        }

        setForm({
            society_admin_name: "",
            society_id: "",
            email: "",
            phone: "",
            password_hash: "",
        });
        setError("");
    }, [society, societies]);

    const handleChange = (e) => {
        if (readOnly) return;
        setError("");

        setForm({
            ...form,
            [e.target.name]: e.target.value,
        });
    };

    const loadSocietyOptions = async (inputValue) => {
        const societies = await getSocieties(inputValue);

        return societies.map((item) => ({
            value: item.society_id,
            label: item.society_name,
        }));
    };

    const selectedSocietyOption = form.society_id
        ? {
              value: form.society_id,
              label:
                  society?.society_name ||
                  societies.find(
                      (item) => String(item.society_id) === String(form.society_id)
                  )?.society_name ||
                  "",
          }
        : null;

    const handleSubmit = async (e) => {
        e.preventDefault();

        const parsed = societyAdminSchema({ passwordRequired: !society }).safeParse(form);
        if (!parsed.success) {
            setError(zodErrorToFirstMessage(parsed.error));
            return;
        }

        try {
            const payload = parsed.data;

            if (society) {
                await updateSocietyAdmin(society.user_id, payload);
            } else {
                await createSocietyAdmin(payload);
            }

            onSuccess();
        } catch (err) {
            console.error(err);
            const apiMessage =
                err?.data?.error ||
                err?.data?.message ||
                err?.message;
            setError(apiMessage || "Unable to save society admin right now.");
        }
    };

    return (
        <form onSubmit={handleSubmit} className={styles.form}>
            {error && (
                <div className={styles.error}>
                    {error}
                </div>
            )}

            <TextInput
                name="society_admin_name"
                value={form.society_admin_name}
                onChange={handleChange}
                disabled={readOnly}
                placeholder="Society Admin Name"
                required
            />

            <div className={styles.selectWrap}>
                <AsyncSelect
                    cacheOptions
                    defaultOptions={societies.map((item) => ({
                        value: item.society_id,
                        label: item.society_name,
                    }))}
                    loadOptions={loadSocietyOptions}
                    value={selectedSocietyOption}
                    onChange={(option) =>
                        setForm((prev) => ({
                            ...prev,
                            society_id: option ? option.value : "",
                        }))
                    }
                    isDisabled={readOnly}
                    placeholder="Search society name..."
                    styles={selectStyles}
                    menuPortalTarget={
                        typeof document !== "undefined" ? document.body : null
                    }
                    menuPosition="fixed"
                    classNamePrefix="react-select"
                    isClearable
                />
            </div>

            <div className={styles.row2}>
                <TextInput
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    disabled={readOnly}
                    placeholder="Email"
                    required
                />

                <TextInput
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    disabled={readOnly}
                    placeholder="Phone"
                    maxLength={10}
                    required
                />
            </div>

            {!readOnly && (
                <PasswordField
                    name="password_hash"
                    value={form.password_hash}
                    onChange={handleChange}
                    placeholder={society ? "New Password (optional)" : "Password"}
                    required={!society}
                />
            )}

            {!readOnly && (
                <div className={styles.actions}>
                    <Button type="submit" variant="primary">
                        {society ? "Update Society Admin" : "Create Society Admin"}
                    </Button>
                </div>
            )}

        </form>
    );
};  
export default SocietyAdminForm;
